/*
 * Presentation helpers shared by the web client and the mobile app.
 *
 * The API returns money as a decimal string (numeric, never a float, Stage 1C
 * TR-04), so formatting operates on strings and never multiplies or divides
 * an amount. We only group digits and prepend a symbol.
 *
 * Moved here from web/src/lib/format.ts in Phase 0 of the mobile app; nothing
 * in this file may touch the DOM or any platform API.
 */

/** Formats a money string like "25000.00" as "KES 25,000". */
export function money(value: string | number | null | undefined, currency = 'KES'): string {
  if (value === null || value === undefined || value === '') return '-';
  const s = typeof value === 'number' ? value.toFixed(2) : value;
  const negative = s.startsWith('-');
  const [whole, frac = '00'] = s.replace('-', '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Whole shillings read cleanest; show cents only when they are non-zero.
  const cents = frac !== '00' ? `.${frac.slice(0, 2)}` : '';
  return `${negative ? '-' : ''}${currency} ${grouped}${cents}`;
}

/** Integer with thousands separators, unit counts, tenant counts. */
export function count(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  const n = typeof value === 'string' ? Number(value) : value;
  return n.toLocaleString('en-KE');
}

/** A rate given as 0..1 or 0..100 rendered as a whole-number percent. */
export function percent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  let n = typeof value === 'string' ? Number(value) : value;
  if (n <= 1) n *= 100;
  return `${Math.round(n)}%`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO date/timestamp to "14 Aug 2026". */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * A subscription's renewal date, for the "Renews" column and the like.
 *
 * A free plan never renews: registration sets its current_period_end to
 * `now() + interval '100 years'` as a "does not expire" sentinel (see the
 * server's auth.ts). Printed with shortDate that reads as "27 Aug 2126", which
 * looks like a bug rather than "never". So anything more than fifty years out is
 * shown as "Never" instead of a literal far-future year.
 */
export function renewDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const FIFTY_YEARS_MS = 50 * 365.25 * 24 * 60 * 60 * 1000;
  if (d.getTime() - Date.now() > FIFTY_YEARS_MS) return 'Never';
  return shortDate(value);
}

/** A relative "3 days ago" for recent activity, falling back to a short date. */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return shortDate(value);
}

/** Title-cases a snake/kebab token like "under_maintenance" → "Under maintenance". */
export function humanise(token: string | null | undefined): string {
  if (!token) return '-';
  const s = token.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* -------------------------------------------------------------------------- */
/* Grand totals for a report                                                  */
/*                                                                            */
/* One definition, shared by the web table, the owner table, the PDF and the  */
/* Excel export, so the total under a column is the same number in all four    */
/* and can never drift the way four separate sums would.                      */
/* -------------------------------------------------------------------------- */

// A column is summed when its NAME marks it as money (or an additive count) and
// its VALUES are numbers. Names carry the intent — a "rate", a "percent", a
// "days" count or an id "number" is numeric but must never be added up, and a
// date or a name is not numeric so it is skipped on its value alone.
const TOTAL_MONEY = /(amount|rent|balance|paid|collected|expected|income|expense|arrears|charge|deposit|total|cost|price|value|net|debit|credit|share|fee|vat|withheld|remitted)/i;
const TOTAL_COUNT = /^(units|payments|invoices|credit_notes|jobs|completed|outstanding|occupied|vacant)$/i;
const TOTAL_NEVER = /(rate|percent|_pct|days?|number|year|version|grace|due_day|aging|band)/i;
const TOTAL_NUMERIC = /^-?\d+(\.\d+)?$/;

/**
 * The column-wise sums for a report, or null when there is nothing to total
 * (fewer than two rows, or no summable column). Summed in integer cents so a
 * long column of money does not drift on the last decimal.
 */
export function reportTotals(
  columns: string[], rows: Record<string, unknown>[],
): Record<string, number> | null {
  if (rows.length < 2) return null;
  const out: Record<string, number> = {};
  let any = false;
  for (const c of columns) {
    if (TOTAL_NEVER.test(c)) continue;
    if (!(TOTAL_MONEY.test(c) || TOTAL_COUNT.test(c))) continue;
    const vals = rows.map(r => r[c]);
    const present = vals.filter(v => v !== null && v !== undefined && v !== '');
    if (present.length === 0) continue;
    // Every value that IS present must be a number, or this is not a money
    // column whatever its name suggests, and a partial sum would mislead.
    if (!present.every(v => TOTAL_NUMERIC.test(String(v)))) continue;
    let cents = 0;
    for (const v of present) cents += Math.round(Number(v) * 100);
    out[c] = cents / 100;
    any = true;
  }
  return any ? out : null;
}

/**
 * The rows a table should render with a grand-total row appended, and whether
 * one was added. The label "Total" lands in the first column that is NOT being
 * summed (the date or name column), so it never sits on top of a figure.
 */
export function withTotalRow(
  columns: string[], rows: Record<string, unknown>[],
): { rows: Record<string, unknown>[]; hasTotal: boolean } {
  const totals = reportTotals(columns, rows);
  if (!totals) return { rows, hasTotal: false };
  const row: Record<string, unknown> = {};
  let labelled = false;
  for (const c of columns) {
    if (c in totals) row[c] = totals[c];
    else if (!labelled) { row[c] = 'Total'; labelled = true; }
    else row[c] = '';
  }
  if (!labelled) row[columns[0]] = 'Total';
  return { rows: [...rows, row], hasTotal: true };
}
